// Step-by-step cleaning (sequential protocols): settings, editor and progress.
export const housekeepingProtocol = {
  // ── Board header button (formerly "Reminders") ──
  settingsBtn: "Settings",
  settingsTitle: "Cleaning mode and reminders",

  // ── Settings drawer ──
  drawer: {
    title: "Cleaning settings",
    subtitle: "How your team works at each property and when they get notified.",
    tabMode: "Cleaning mode",
    tabReminders: "Reminders",
  },

  mode: {
    label: "How is cleaning done?",
    standardTitle: "Standard",
    standardDesc: "Mark as clean, photos and code, as before.",
    sequentialTitle: "Step by step (sequential)",
    sequentialDesc: "Protocol with stages and tasks; a stage cannot be started until the previous one is finished.",
    recommended: "New",
    protocolLabel: "Protocol",
    protocolPlaceholder: "Choose a protocol…",
    noProtocols: "You have no protocols yet. Create one from the SOP template or start from scratch.",
    protocolMeta: "{etapas} stages · {tareas} tasks · v{version}",
    usedIn: "Used by {count} properties",
    fromTemplate: "Create from SOP template",
    newProtocol: "New protocol",
    editProtocol: "Edit protocol",
    clone: "Clone to other properties",
    creating: "Creating…",
    templateName: "SOP protocol",
    newProtocolName: "New protocol",
    needProtocol: "Choose or create a protocol to enable step-by-step cleaning.",
    allNote: "The selected mode will be applied to all your properties.",
    saved: "Cleaning mode saved",
    errorLoad: "Could not load protocols",
    errorSave: "Could not save. Please try again.",
    errorCreate: "Could not create the protocol",
    howTitle: "How does it work?",
    howText: "On mobile, staff will see the stages in order. Each task is checked off when done and, if it requires a photo, it cannot be checked without one. The next stage unlocks when the previous one is finished.",
  },

  settings: {
    title: "Cleaning settings",
    subtitle: "Pick a property on the left to see and change how it's cleaned.",
    search: "Search property…",
    loading: "Loading properties…",
    tagStd: "Standard",
    tagSteps: "Step by step",
    tagRule: "Every day | Every {count} days",
    tagBell: "Alerts",
    listFoot: "{steps} step by step · {rules} with long-stay rule · {total} properties",
    pick: "Pick a property from the list.",
    unsaved: "Unsaved",
    copyBtn: "Copy to other properties",
    copyHelp: "Everything from this property (how it's cleaned, rules and alerts) will be copied to the ones you tick.",
    copyDo: "Copy to {count} property | Copy to {count} properties",
    copied: "Copied to {count} property | Copied to {count} properties",
    howTitle: "How is it cleaned?",
    howHelp: "How the team works in this property.",
    pickProtocol: "Choose a protocol…",
    tasks: "{count} task | {count} tasks",
    rulesTitle: "Cleaning rules",
    rulesHelp: "When a room becomes “To clean” on its own, even if nobody checks out that day.",
    longStayRule: "Clean during long stays",
    longStayRuleHelp: "If a guest stays several days, the room goes back to “To clean” every so often.",
    longStayExample: "Example: checks in on Monday and stays a week → it will show “To clean” every day. | Example: checks in on Monday and stays a week → it will show “To clean” every {count} days, counting from the last cleaning.",
    alertsTitle: "Team alerts",
    alertsHelp: "Phone messages so nothing is left undone. They don't change the cleaning list.",
    arrivalsAlert: "Alert about arrivals and departures not cleaned",
    arrivalsAlertHelp: "If a room with an upcoming check-in or check-out isn't ready, the team is alerted.",
    longStayAlsoAlerts: "The long-stay rule also alerts the team, at this time and to these people.",
    save: "Save changes",
    saveError: "Couldn't save",
    discard: "There are unsaved changes. Discard them?",
  },
  clone: {
    title: "Clone \"{name}\"",
    help: "Choose which properties to copy it to. They will switch to \"Step by step\" mode with their copy assigned.",
    onePerProperty: "One independent copy per property",
    onePerPropertyHelp: "This way each property can adjust its protocol without affecting the others. If disabled, they all share a single copy.",
    selectAll: "All",
    selectNone: "None",
    confirm: "Clone to {count}",
    cancel: "Cancel",
    done: "Protocol cloned to {count} property | Protocol cloned to {count} properties",
    error: "Could not clone the protocol",
    current: "(current)",
  },

  // ── Protocol editor ──
  editor: {
    back: "Housekeeping",
    namePlaceholder: "Protocol name",
    descriptionPlaceholder: "Short description (optional): what kind of cleaning it is for…",
    version: "v{n}",
    viewList: "List",
    viewFlow: "Flow",
    addStage: "Add stage",
    save: "Save",
    saving: "Saving…",
    saved: "Protocol saved",
    errorSave: "Could not save the protocol",
    unsaved: "Unsaved changes",
    allSaved: "All saved",
    loading: "Loading protocol…",
    notFound: "This protocol could not be found.",
    summary: "{etapas} stages · {tareas} tasks · ~{minutos} min",
    leaveTitle: "Leave without saving?",
    leaveMessage: "You have changes to this protocol that have not been saved yet. If you leave now, they will be lost.",
    leaveConfirm: "Leave without saving",
    leaveCancel: "Keep editing",
    emptyTitle: "This protocol is empty",
    emptyText: "Add the first stage (for example \"Air out and strip linen\") and its tasks inside it.",
  },

  stage: {
    defaultTitle: "Stage {n}",
    number: "Stage {n}",
    titlePlaceholder: "Stage name",
    descriptionPlaceholder: "Stage description (optional)",
    minutes: "min",
    minutesTitle: "Estimated minutes",
    duplicate: "Duplicate stage",
    copySuffix: "(copy)",
    delete: "Delete stage",
    deleteTitle: "Delete \"{name}\"?",
    deleteMessage: "The stage and its {count} tasks will be deleted.",
    deleteConfirm: "Delete",
    cancel: "Cancel",
    menu: "Stage options",
    tasks: "no tasks | 1 task | {count} tasks",
    addTask: "Add task",
    emptyTasks: "Drag tasks here or add a new one",
    dragHandle: "Drag to reorder",
  },

  task: {
    defaultTitle: "New task",
    editorTitle: "Edit task",
    inStage: "In \"{stage}\"",
    titleLabel: "Task",
    titlePlaceholder: "What needs to be done",
    instructionsLabel: "Instructions",
    instructionsPlaceholder: "How to do it right: products, order, details that must not be forgotten…",
    photoLabel: "Evidence photo",
    photoNone: "None",
    photoOptional: "Optional",
    photoRequired: "Required",
    minPhotosLabel: "Minimum photos",
    critical: "Critical task",
    criticalHelp: "Highlighted in red and cannot be skipped.",
    delete: "Delete task",
    close: "Close",
    badgePhoto: "Photo",
    badgePhotoReq: "Photo ×{n}",
    badgeCritical: "Critical",
    untitled: "(untitled)",
  },

  flow: {
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    fit: "Fit",
    autoLayout: "Auto layout",
    hint: "Drag the background to pan · wheel to zoom · drag tasks between stages",
    minutes: "{n} min",
  },

  // ── Board progress ──
  board: {
    stepPill: "Step {hechas}/{total} · {etapa}",
    stepDone: "Steps {hechas}/{total} · completed",
    stepTip: "Step-by-step cleaning in progress{persona}",
    by: " · {name}",
    sequentialChip: "Step by step",
    sequentialTip: "This property cleans with a step-by-step protocol. It has not been started yet.",
  },

  // ── Space drawer section ──
  runs: {
    title: "Step-by-step cleaning",
    loading: "Loading runs…",
    empty: "There are no step-by-step cleanings for this space yet.",
    open: "View details",
    latest: "Latest",
    history: "Previous",
    hide: "Hide details",
    progress: "{hechas}/{total} tasks",
    currentStage: "Now: {etapa}",
    startedAt: "Started {date}",
    finishedAt: "Finished {date}",
    by: "by {name}",
    locked: "Locked",
    stageDone: "Completed",
    critical: "Critical",
    noteLabel: "Note",
    loadError: "Could not load the run",
    status: {
      in_progress: "In progress",
      completed: "Completed",
      done: "Completed",
      cancelled: "Cancelled",
      abandoned: "Abandoned",
      pending: "Pending",
    },
  },
};
